/**
 * queryLimits.js — shared Firestore result caps + truncation detection (pure).
 *
 * Firestore gives no "there are more results" signal: a query that returns
 * exactly `limit` documents is indistinguishable from one that returned
 * everything. Every dashboard query is capped, so without a check a wide date
 * range silently yields a PARTIAL slice — and every number computed from it
 * (avg prep time, quality score, category revenue) is quietly wrong while
 * looking perfectly healthy.
 *
 * A full page is therefore our proxy for "this range overflowed". It can false
 * positive when a range happens to hold exactly `limit` rows; that is the safe
 * direction to be wrong in — we'd rather say "narrow the range" once too often
 * than chart a truncated quarter as if it were complete.
 *
 * The real fix for wide ranges is the pre-aggregated daily rollups; these caps
 * exist to bound browser memory and Firestore reads until a range is served
 * from those instead.
 */

export const QUERY_LIMITS = {
  historyRecords: 1000,   // completed tickets — History + Quality tabs
  ticketsInRange: 2000,   // Analytics / All Tickets / Category Sales
  dayRecords:      500,   // a single calendar day — Prep Forecast
};

/**
 * Describe whether a snapshot hit its cap.
 *
 * `fetched` must be the RAW snapshot size (before any client-side filtering),
 * because the cap is applied by Firestore before our filters run — filtering
 * first would mask an overflow behind a small post-filter count.
 *
 * @param {object}  args
 * @param {number}  args.fetched — raw snapshot.size
 * @param {number}  args.limit   — the cap passed to limit()
 * @param {number} [args.kept]   — rows surviving client-side filters (defaults to fetched)
 * @returns {{truncated: boolean, fetched: number, limit: number, kept: number}}
 */
export function truncationInfo({ fetched = 0, limit = 0, kept } = {}) {
  const f = Number.isFinite(fetched) ? Math.max(0, fetched) : 0;
  const l = Number.isFinite(limit) ? Math.max(0, limit) : 0;
  return {
    truncated: l > 0 && f >= l,
    fetched: f,
    limit: l,
    kept: Number.isFinite(kept) ? Math.max(0, kept) : f,
  };
}

/** Neutral "nothing loaded / nothing capped" value, so callers never see undefined. */
export const NO_TRUNCATION = Object.freeze({ truncated: false, fetched: 0, limit: 0, kept: 0 });
